/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */

export interface UaPlaceholder {
  variablePath: string;
  reference: string;
  type: string;
}

export interface UaVariable {
  variablePath: string;
  property?: string;
  dataType: string;
  optional?: boolean;
  hasUnit?: boolean;
  hasRange?: boolean;
  field?: string; // for structures
}

export interface UaEnumValues {
  [key: number]: string;
}

export interface UaVariableEnum extends UaVariable {
  enumValues: UaEnumValues;
}

export interface Template {
  namespaceUris: string[];
  header: Header;
  imagesConfig: ImagesConfig;
  stacklight?: Stacklight;
  timeline?: Timeline;
  sections: Section[];
}

export interface Header {
  nameShowStaticText?: boolean;
  nameVariable?: UaVariable;
  nameStaticText?: string;
  modelShowStaticText?: boolean;
  modelVariable?: UaVariable;
  modelStaticText?: string;
}

export interface ImagesConfig {
  defaultImagePathClassifierVariable?: UaVariable;
  imageDefaults: ImageDefaults;
}

export interface ImageDefaults {
  [key: string]: string;
}

export interface Stacklight {
  lampPlaceholders?: UaPlaceholder[];
  signalVariable?: UaVariable;
  signalOnValue?: string;
  signalOffValue?: string;
  sortingVariable?: UaVariable;
  colorVariable?: UaVariable;
  colorValues: ColorValues;
}

export interface Timeline {
  variable?: UaVariable;
  colorValues: ColorValues;
}

export interface ColorValues {
  [key: string]: string;
}

export interface Section {
  id: string;
  rows: SectionRow[];
  optional?: boolean;
  validationNodePath?: string;
  placeholders?: UaPlaceholder[];
  // available if placeholder row :: START
  titleVariable?: UaVariable;
  // available if placeholder row :: END
  // available if not placeholder section :: START
  title?: string;
  // available if not placeholder section :: END
  condition?: RenderingCondition;
}

export interface SectionRow {
  id: string;
  elements: SectionRowElement[];
  optional?: boolean;
  validationNodePath?: string;
  placeholders?: UaPlaceholder[];
  // available if placeholder row :: START
  titleVariable?: UaVariable;
  // available if placeholder row :: END
  // available if not placeholder row :: START
  title?: string;
  // available if not placeholder row :: START
  condition?: RenderingCondition;
}

export enum SectionRowElementTypeEnum {
  TEXT,
  CHART,
  BADGE,
  TABLE,
  LIST,
}

export interface SectionRowElement {
  id: string;
  type: SectionRowElementTypeEnum;
  valueVariable?: UaVariable;
  placeholders?: UaPlaceholder[];
  // available if placeholder element :: START
  labelVariable?: UaVariable;
  // available if placeholder element :: END
  // available if not placeholder element :: START
  label?: string;
  // available if not placeholder element :: END
  condition?: RenderingCondition;
}

export interface SectionRowElementText extends SectionRowElement {
  showAsLink?: boolean;
}

export interface SectionRowElementChart extends SectionRowElement {
  customRangeHigh?: number;
  customRangeLow?: number;
}

export interface SectionRowElementBadge extends SectionRowElement {
  badgeValues: SectionRowElementBadgeValue[];
}

export interface SectionRowElementBadgeValue {
  backgroundColor: string;
  textColor: string;
  value: string;
  label?: string;
}

export interface SectionRowElementTable extends SectionRowElement {
  rowPlaceholders?: UaPlaceholder[];
  columns: SectionRowElementTableColumn[];
}

export interface SectionRowElementTableColumn {
  id: string;
  label: string;
  valueVariable?: UaVariable;
}

export interface SectionRowElementList extends SectionRowElementText {}

export interface RenderingCondition {
  valueVariable?: UaVariable;
  values: string[];
}
